import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Clipboard,
  ExternalLink,
  CircleStop,
  Download,
  Eye,
  FileUp,
  Inbox,
  MailPlus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Upload,
} from 'lucide-react';
import { api, downloadFile, uploadFile } from '../api';
import type { BackgroundJob, Mailbox, Message, Pagination } from '../types';
import { formatTime, Modal, PageHeader, statusLabel } from '../components/Common';

interface MailboxResponse { items: Mailbox[]; pagination: Pagination }

const emptyPagination: Pagination = { page: 1, pageSize: 20, total: 0, totalPages: 1 };

/**
 * 功能：渲染邮箱导入、批量操作、Token 刷新、自检和邮件查看管理界面。
 * 参数：notify（全局通知回调）。
 */
export function MailboxesScreen({ notify }: { notify: (message: string, type?: 'success' | 'error') => void }) {
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [pagination, setPagination] = useState<Pagination>(emptyPagination);
  const [filters, setFilters] = useState({ search: '', provider: '', category: '', health: '', platform: '' });
  const [options, setOptions] = useState<{ providers: string[]; categories: string[]; platforms: Array<{ slug: string; name: string }> }>({ providers: [], categories: [], platforms: [] });
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [singleOpen, setSingleOpen] = useState(false);
  const [messageMailbox, setMessageMailbox] = useState<Mailbox | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeJob, setActiveJob] = useState<BackgroundJob | null>(null);
  const pageCursors = useRef<number[]>([0]);

  /** 功能：按当前筛选、页码与主键游标加载邮箱列表；参数：page（界面页码）；cursor（该页起始游标）。 */
  const load = useCallback(async (page = 1, cursor = 0): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (cursor) params.set('cursor', String(cursor));
      if (appliedSearch) params.set('search', appliedSearch);
      if (filters.provider) params.set('provider', filters.provider);
      if (filters.category) params.set('category', filters.category);
      if (filters.health) params.set('health', filters.health);
      if (filters.platform) params.set('platform', filters.platform);
      const data = await api<MailboxResponse>(`/api/mailboxes?${params}`);
      setOptions(await api('/api/mailboxes/filters'));
      setMailboxes(data.items);
      setPagination(data.pagination);
      setSelected(new Set());
    } catch (error) {
      notify(error instanceof Error ? error.message : '邮箱列表加载失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, filters.provider, filters.category, filters.health, filters.platform, notify]);

  useEffect(() => { pageCursors.current = [0]; void load(1, 0); }, [load]);

  const providers = useMemo(() => options.providers, [options.providers]);
  const allSelected = mailboxes.length > 0 && mailboxes.every((mailbox) => selected.has(mailbox.id));

  /** 功能：切换当前页所有邮箱的选择状态；参数：无。 */
  const toggleAll = (): void => {
    setSelected(allSelected ? new Set() : new Set(mailboxes.map((mailbox) => mailbox.id)));
  };

  /** 功能：切换单个邮箱选择状态；参数：id（邮箱主键）。 */
  const toggleOne = (id: number): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /** 功能：导出全部或已选邮箱；参数：format（txt/csv/json）。 */
  const exportData = async (format = 'csv'): Promise<void> => {
    try {
      const params = new URLSearchParams({ format });
      if (selected.size) params.set('ids', [...selected].join(','));
      await downloadFile(`/api/mailboxes/export?${params}`, `smartmail-mailboxes.${format}`);
      notify(`已导出${selected.size ? ` ${selected.size} 个` : '全部'}邮箱`);
    } catch (error) { notify(error instanceof Error ? error.message : '导出失败', 'error'); }
  };

  /** 功能：创建服务器后台邮箱任务并开始轮询进度；参数：path（任务端点）。 */
  const startMailboxJob = async (path: string): Promise<void> => {
    setRefreshing(true);
    try {
      const job = await api<BackgroundJob>(path, { method: 'POST', body: JSON.stringify({ ids: [...selected] }) });
      setActiveJob(job);
      notify(`后台任务 #${job.id} 已进入队列`);
    } catch (error) {
      notify(error instanceof Error ? error.message : '后台任务创建失败', 'error');
    } finally { setRefreshing(false); }
  };

  useEffect(() => {
    if (!activeJob || !['pending', 'running'].includes(activeJob.status)) return undefined;
    const timer = window.setInterval(() => {
      void api<BackgroundJob>(`/api/jobs/${activeJob.id}`)
        .then(setActiveJob)
        .catch((error) => notify(error instanceof Error ? error.message : '任务进度读取失败', 'error'));
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [activeJob?.id, activeJob?.status, notify]);

  useEffect(() => {
    if (!activeJob || !['completed', 'failed', 'cancelled'].includes(activeJob.status)) return;
    if (activeJob.status === 'completed') notify(`任务 #${activeJob.id} 完成：成功 ${activeJob.succeeded}，失败 ${activeJob.failed}`, activeJob.failed ? 'error' : 'success');
    else notify(`任务 #${activeJob.id} 已${activeJob.status === 'cancelled' ? '取消' : '失败'}`, 'error');
    void load(pagination.page, pageCursors.current[pagination.page - 1] || 0);
  }, [activeJob?.status]);

  /** 功能：对已选邮箱创建 Token 刷新后台任务；参数：无。 */
  const refreshTokens = async (): Promise<void> => {
    if (!selected.size) { notify('请先选择需要刷新 Token 的邮箱', 'error'); return; }
    await startMailboxJob('/api/mailboxes/refresh-token');
  };

  /** 功能：对已选邮箱或整个可验证邮箱池执行收信自检；参数：无。 */
  const selfCheck = async (): Promise<void> => {
    await startMailboxJob('/api/mailboxes/self-check');
  };

  /** 功能：请求取消当前运行中的后台任务；参数：无。 */
  const cancelJob = async (): Promise<void> => {
    if (!activeJob) return;
    const job = await api<BackgroundJob>(`/api/jobs/${activeJob.id}/cancel`, { method: 'POST' });
    setActiveJob(job);
  };

  /** 功能：通过服务端返回的游标进入下一页；参数：无。 */
  const nextPage = (): void => {
    if (!pagination.nextCursor) return;
    pageCursors.current[pagination.page] = pagination.nextCursor;
    void load(pagination.page + 1, pagination.nextCursor);
  };

  /** 功能：使用已记录的页首游标返回上一页；参数：无。 */
  const previousPage = (): void => {
    const page = Math.max(1, pagination.page - 1);
    void load(page, pageCursors.current[page - 1] || 0);
  };

  /** 功能：删除已选择邮箱及其平台关联数据；参数：无。 */
  const deleteSelected = async (): Promise<void> => {
    if (!selected.size || !window.confirm(`确认删除已选 ${selected.size} 个邮箱？关联平台记录也会删除。`)) return;
    try {
      const result = await api<{ deleted: number }>('/api/mailboxes/batch-delete', { method: 'POST', body: JSON.stringify({ ids: [...selected] }) });
      notify(`已删除 ${result.deleted} 个邮箱`);
      await load(1);
    } catch (error) { notify(error instanceof Error ? error.message : '删除失败', 'error'); }
  };

  return (
    <div className="page mailbox-page">
      <PageHeader
        eyebrow="MAILBOX ASSET INVENTORY"
        title="邮箱管理"
        description="仅管理手动添加和批量导入的邮箱；域名邮箱与失效邮箱分别在独立入口管理。"
        actions={<><button className="button button-secondary" onClick={() => setImportOpen(true)}><Upload size={16} />批量上传</button><button className="button button-primary" onClick={() => setSingleOpen(true)}><MailPlus size={16} />添加邮箱</button></>}
      />

      <section className="mail-toolbar">
        <form className="search-box" onSubmit={(event) => { event.preventDefault(); setAppliedSearch(filters.search); }}>
          <Search size={16} /><input value={filters.search} onChange={(event) => setFilters({ ...filters, search: event.target.value })} placeholder="搜索邮箱或备注" aria-label="搜索邮箱" /><button type="submit">搜索</button>
        </form>
        <div className="filter-group">
          <select value={filters.provider} onChange={(event) => setFilters({ ...filters, provider: event.target.value })} aria-label="邮箱类型"><option value="">全部类型</option>{providers.map((provider) => <option key={provider} value={provider}>{provider}</option>)}</select>
          <select value={filters.category} onChange={(event) => setFilters({ ...filters, category: event.target.value })} aria-label="邮箱分类"><option value="">全部分类</option>{options.categories.map(category => <option key={category} value={category}>{category}</option>)}</select>
          <select value={filters.platform} onChange={(event) => setFilters({ ...filters, platform: event.target.value })} aria-label="使用种类"><option value="">全部种类</option>{options.platforms.map(platform => <option key={platform.slug} value={platform.slug}>{platform.name}</option>)}</select>
          <select value={filters.health} onChange={(event) => setFilters({ ...filters, health: event.target.value })} aria-label="健康状态"><option value="">全部状态</option><option value="healthy">健康</option><option value="warning">待复检</option><option value="damaged">已损坏</option><option value="unknown">未检查</option></select>
        </div>
      </section>

      <section className="batch-strip">
        <div><strong>{selected.size}</strong><span>已选择</span></div>
        <button onClick={() => void refreshTokens()} disabled={refreshing}><RefreshCw size={15} className={refreshing ? 'spin' : ''} />刷新 Token</button>
        <button onClick={() => void selfCheck()} disabled={refreshing}><ShieldCheck size={15} />邮箱自检</button>
        <button onClick={() => void exportData('txt')}><Download size={15} />注册机 TXT</button>
        <button onClick={() => void exportData('csv')}><FileUp size={15} />导出 CSV</button>
        <button className="danger-action" onClick={() => void deleteSelected()} disabled={!selected.size}><Trash2 size={15} />批量删除</button>
      </section>

      {activeJob ? <section className={`job-progress job-${activeJob.status}`} aria-live="polite"><div><span>JOB #{activeJob.id} / {activeJob.type === 'mailbox_self_check' ? '邮箱自检' : 'TOKEN REFRESH'}</span><strong>{activeJob.status === 'completed' ? '已完成' : activeJob.status === 'cancelled' ? '已取消' : activeJob.status === 'failed' ? '失败' : '后台执行中'}</strong></div><div className="job-progress-track"><span style={{ width: `${activeJob.progress}%` }} /></div><code>{activeJob.processed.toLocaleString()} / {activeJob.total.toLocaleString()} · 成功 {activeJob.succeeded.toLocaleString()} · 失败 {activeJob.failed.toLocaleString()}</code>{['pending', 'running'].includes(activeJob.status) ? <button className="icon-button" onClick={() => void cancelJob()} aria-label="取消后台任务" title="取消后台任务"><CircleStop size={16} /></button> : <button className="icon-button" onClick={() => setActiveJob(null)} aria-label="关闭任务进度" title="关闭任务进度"><ChevronRight size={16} /></button>}</section> : null}

      <section className="data-table-wrap">
        <table className="data-table mailbox-table">
          <thead><tr><th className="check-column"><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="选择当前页全部邮箱" /></th><th>邮箱资产</th><th>类型 / 分类</th><th>健康状态</th><th>平台占用</th><th>最近检查</th><th className="action-column">操作</th></tr></thead>
          <tbody>
            {mailboxes.map((mailbox) => (
              <tr key={mailbox.id} className={selected.has(mailbox.id) ? 'row-selected' : ''}>
                <td><input type="checkbox" checked={selected.has(mailbox.id)} onChange={() => toggleOne(mailbox.id)} aria-label={`选择 ${mailbox.email}`} /></td>
                <td><div className="mail-identity"><strong>{mailbox.email}</strong><span><i className={mailbox.hasRefreshToken ? 'credential-on' : ''} />{mailbox.hasRefreshToken ? 'OAuth 凭据就绪' : '仅基础凭据'} · {mailbox.source}</span></div></td>
                <td><div className="type-cell"><strong>{mailbox.provider}</strong><code>{mailbox.category}</code></div></td>
                <td><span className={`status status-${mailbox.healthStatus}`}><i />{statusLabel(mailbox.healthStatus)}</span>{mailbox.failureCount ? <small className="failure-count">失败 {mailbox.failureCount} 次</small> : null}</td>
                <td><div className="usage-badges">{Object.entries(mailbox.platformUsage).map(([slug, status]) => <span key={slug} className={status === 'success' ? 'used' : ''} title={`${slug}: ${status}`}>{options.platforms.find(platform => platform.slug === slug)?.name || slug}</span>)}{!Object.keys(mailbox.platformUsage).length ? <span>未使用</span> : null}</div></td>
                <td><div className="time-cell"><strong>{formatTime(mailbox.lastCheckedAt)}</strong><span>{mailbox.lastError || '无异常记录'}</span></div></td>
                <td><button className="icon-button" onClick={() => setMessageMailbox(mailbox)} aria-label={`查看 ${mailbox.email} 的邮件`} title="查看邮件"><Eye size={17} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading ? <div className="table-state"><RefreshCw className="spin" size={20} />正在读取邮箱资产</div> : null}
        {!loading && !mailboxes.length ? <div className="table-state empty"><Inbox size={25} /><strong>邮箱池为空</strong><span>添加单个邮箱或上传 1.10.2 邮箱池文件。</span></div> : null}
      </section>

      <div className="table-footer">
        <span>共 {pagination.total.toLocaleString()} 个邮箱 · 第 {pagination.page}/{pagination.totalPages} 页</span>
        <div><button className="icon-button" disabled={pagination.page <= 1} onClick={previousPage} aria-label="上一页"><ChevronLeft size={17} /></button><button className="icon-button" disabled={!pagination.hasMore} onClick={nextPage} aria-label="下一页"><ChevronRight size={17} /></button></div>
      </div>

      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onComplete={async (message) => { setImportOpen(false); notify(message); await load(1); }} notify={notify} />
      <SingleMailboxModal open={singleOpen} onClose={() => setSingleOpen(false)} onComplete={async () => { setSingleOpen(false); notify('邮箱已加入资源池'); await load(1); }} />
      <MessageDrawer mailbox={messageMailbox} onClose={() => setMessageMailbox(null)} notify={notify} />
    </div>
  );
}

/**
 * 功能：渲染 TXT/CSV/JSON 批量邮箱导入对话框。
 * 参数：open（显示状态）；onClose（关闭回调）；onComplete（完成回调）；notify（错误通知）。
 */
function ImportModal({ open, onClose, onComplete, notify }: { open: boolean; onClose: () => void; onComplete: (message: string) => Promise<void>; notify: (message: string, type?: 'success' | 'error') => void }) {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('safe');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  /** 功能：记录待流式上传的 UTF-8 邮箱文件，不在浏览器中读取整份内容；参数：event（文件输入变更事件）。 */
  const selectFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setUploadProgress(0);
  };

  /** 功能：提交批量邮箱文本到后端解析导入；参数：event（表单提交事件）。 */
  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    try {
      let result: { imported: number; updated: number; skipped: number };
      if (file) {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (extension === 'json' && file.size > 12 * 1024 * 1024) throw new Error('大型 JSON 数组请转换为每行一个对象的 .ndjson 文件');
        if (extension === 'json') {
          result = await api('/api/mailboxes/import', { method: 'POST', body: JSON.stringify({ text: await file.text(), format: 'json', defaultCategory: category }) });
        } else {
          const format = extension === 'csv' ? 'csv' : extension === 'ndjson' ? 'ndjson' : 'txt';
          const params = new URLSearchParams({ format, defaultCategory: category });
          result = await uploadFile(`/api/mailboxes/import-stream?${params}`, file, setUploadProgress);
        }
      } else {
        result = await api('/api/mailboxes/import', { method: 'POST', body: JSON.stringify({ text, format: 'auto', defaultCategory: category }) });
      }
      setText('');
      setFile(null);
      setUploadProgress(0);
      if (fileRef.current) fileRef.current.value = '';
      await onComplete(`导入 ${result.imported} 个，更新 ${result.updated} 个，跳过 ${result.skipped} 个`);
    } catch (error) { notify(error instanceof Error ? error.message : '导入失败', 'error'); }
    finally { setLoading(false); }
  };

  return <Modal open={open} title="批量上传邮箱" onClose={onClose} size="lg"><form onSubmit={submit} className="modal-form"><div className="import-grid"><button type="button" className={`upload-drop ${file ? 'file-selected' : ''}`} onClick={() => fileRef.current?.click()}><Upload size={24} /><strong>{file ? file.name : '选择邮箱文件'}</strong><span>{file ? `${(file.size / 1024 / 1024).toFixed(1)} MB · 文件流模式` : 'TXT、CSV、NDJSON 可直接流式导入'}</span></button><input ref={fileRef} type="file" accept=".txt,.csv,.json,.ndjson" hidden onChange={selectFile} /><div className="format-proof"><span>1.10.2 TXT</span><code>email----password----client_id----refresh_token</code><span>CSV / NDJSON</span><code>email,password,client_id,refresh_token,category,notes,provider</code><span>iCloud 收码 TXT（推荐）</span><code>邮箱---url</code><span>iCloud 收码 TXT（三段兼容，中间字段忽略）</span><code>邮箱---任意中间字段---url</code><span>iCloud IMAP TXT（兼容）</span><code>隐私邮箱----App专用密码----真实iCloud邮箱--------safe----备注----icloud</code></div></div>{loading && file ? <div className="upload-progress"><div><span style={{ width: `${uploadProgress}%` }} /></div><code>{uploadProgress < 100 ? `上传 ${uploadProgress}%` : '服务器正在分批写入数据库'}</code></div> : null}<label>默认分类<select value={category} onChange={(event) => setCategory(event.target.value)}><option value="safe">safe</option><option value="pending">待确认</option><option value="no_code">no_code</option></select></label><label>导入内容<textarea value={text} onChange={(event) => { setText(event.target.value); if (event.target.value) setFile(null); }} placeholder="小批量数据可直接粘贴；大文件请从上方选择" rows={10} /></label><div className="modal-actions"><button type="button" className="button button-secondary" onClick={onClose}>取消</button><button className="button button-primary" disabled={(!file && !text.trim()) || loading}><Upload size={16} />{loading ? '正在导入' : '开始导入'}</button></div></form></Modal>;
}

/**
 * 功能：渲染单邮箱添加表单。
 * 参数：open（显示状态）；onClose（关闭回调）；onComplete（保存完成回调）。
 */
function SingleMailboxModal({ open, onClose, onComplete }: { open: boolean; onClose: () => void; onComplete: () => Promise<void> }) {
  const [form, setForm] = useState({ email: '', provider: '', password: '', clientId: '', refreshToken: '', category: 'safe', notes: '' });
  const [loading, setLoading] = useState(false);

  /** 功能：提交单邮箱资产；参数：event（表单提交事件）。 */
  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault(); setLoading(true);
    try { await api('/api/mailboxes', { method: 'POST', body: JSON.stringify(form) }); setForm({ email: '', provider: '', password: '', clientId: '', refreshToken: '', category: 'safe', notes: '' }); await onComplete(); }
    finally { setLoading(false); }
  };
  return <Modal open={open} title="添加邮箱资产" onClose={onClose}><form onSubmit={submit} className="modal-form"><div className="form-row"><label>邮箱地址<input type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="name@outlook.com" /></label><label>邮箱分类<select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}><option value="safe">safe</option><option value="pending">待确认</option><option value="no_code">no_code</option></select></label></div><label>邮箱提供商<select value={form.provider} onChange={(event) => setForm({ ...form, provider: event.target.value })}><option value="">自动识别</option><option value="outlook">Outlook / Microsoft</option><option value="icloud">iCloud 隐私邮箱</option><option value="gmail">Gmail</option><option value="other">其他</option></select></label><label>{form.provider === 'icloud' ? '收码 KEY（pickup 可留空）/ Apple App 专用密码' : '邮箱密码'}<input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder={form.provider === 'icloud' ? 'tok_... 或 xxxx-xxxx-xxxx-xxxx' : ''} /></label><label>{form.provider === 'icloud' ? '收码链接 / 真实 iCloud 登录邮箱' : 'Microsoft Client ID'}<input value={form.clientId} onChange={(event) => setForm({ ...form, clientId: event.target.value })} placeholder={form.provider === 'icloud' ? 'https://flysms.xyz/icloud/pickup#email=...&key=... 或 owner@icloud.com' : ''} /></label>{form.provider !== 'icloud' ? <label>Refresh Token<textarea value={form.refreshToken} onChange={(event) => setForm({ ...form, refreshToken: event.target.value })} rows={3} /></label> : <small>推荐 pickup 模式：只填写邮箱和完整收码链接，KEY 会从链接自动解析；密码框可留空。原 IMAP 模式仍填写真实 iCloud 邮箱与 App 专用密码。iCloud 只在你主动点击“邮箱自检”时参与自检，后台定时自检会跳过 iCloud。</small>}<label>备注<input value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} /></label><div className="modal-actions"><button type="button" className="button button-secondary" onClick={onClose}>取消</button><button className="button button-primary" disabled={loading}><MailPlus size={16} />保存邮箱</button></div></form></Modal>;
}

/**
 * 功能：渲染邮箱文件夹和缓存邮件详情抽屉。
 * 参数：mailbox（当前邮箱）；onClose（关闭回调）；notify（通知回调）。
 */
function MessageDrawer({ mailbox, onClose, notify }: { mailbox: Mailbox | null; onClose: () => void; notify: (message: string, type?: 'success' | 'error') => void }) {
  const [folder, setFolder] = useState('inbox');
  const [messages, setMessages] = useState<Message[]>([]);
  const [syncError, setSyncError] = useState('');
  const [active, setActive] = useState<Message | null>(null);
  const [loading, setLoading] = useState(false);

  /** 功能：读取当前邮箱邮件并按需向服务端同步；参数：refresh（是否先从邮箱服务刷新）。 */
  const loadMessages = useCallback(async (refresh: boolean): Promise<void> => {
    if (!mailbox) return;
    setLoading(true);
    try {
      const data = await api<{ messages: Message[]; syncError: string }>(`/api/mailboxes/${mailbox.id}/messages?folder=${folder}&limit=30&refresh=${refresh ? 1 : 0}`);
      setMessages(data.messages);
      setSyncError(data.syncError);
      setActive(data.messages[0] || null);
      if (refresh && !data.syncError) notify('Mailbox refresh successful');
    } catch (error) { notify(error instanceof Error ? error.message : 'Mailbox refresh failed', 'error'); }
    finally { setLoading(false); }
  }, [mailbox, folder, notify]);

  /** 功能：复制 Claude 登录链接，兼容 HTTP 环境下浏览器剪贴板限制；参数 url：要复制的链接。 */
  const copyActionUrl = async (url: string): Promise<void> => {
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      notify('Claude 链接已复制');
    } catch (error) {
      notify(error instanceof Error ? `复制失败：${error.message}` : '复制失败，请手动复制灰色文本框中的链接', 'error');
    }
  };

  useEffect(() => { if (mailbox) void loadMessages(false); }, [mailbox, folder, loadMessages]);
  if (!mailbox) return null;

  return <div className="drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="message-drawer" aria-label={`${mailbox.email} 邮件`}>
      <div className="drawer-head">
        <div><span>MAIL INSPECTOR</span><h2>{mailbox.email}</h2><p>{mailbox.provider} · {statusLabel(mailbox.healthStatus)}</p></div>
        <button className="icon-button" onClick={onClose} aria-label="关闭邮件抽屉"><ChevronRight size={19} /></button>
      </div>
      <div className="drawer-toolbar">
        <select value={folder} onChange={(event) => setFolder(event.target.value)} aria-label="邮件文件夹">
          <option value="inbox">收件箱</option>
          <option value="junkemail">垃圾邮件</option>
          <option value="deleteditems">已删除</option>
          <option value="sentitems">已发送</option>
        </select>
        <button className="button button-secondary" onClick={() => void loadMessages(true)} disabled={loading}><RefreshCw size={15} className={loading ? 'spin' : ''} />刷新</button>
      </div>
      {syncError ? <div className="sync-warning">{syncError}，可稍后再次刷新。</div> : null}
      <div className="mail-reader">
        <div className="message-list">
          {messages.map((message) => <button key={message.id} className={active?.id === message.id ? 'active' : ''} onClick={() => setActive(message)}>
            <span>{message.sender || '未知发件人'}</span>
            <strong>{message.subject || '无主题'}</strong>
            <small>{formatTime(message.receivedAt)}{message.actionUrl ? <code>link</code> : message.code ? <code>{message.code}</code> : null}</small>
          </button>)}
          {!messages.length && !loading ? <div className="drawer-empty"><Inbox size={23} /><span>当前文件夹暂无邮件</span></div> : null}
        </div>
        <div className="message-content">
          {active ? <>
            <div className="message-meta"><span>FROM</span><strong>{active.sender || '未知发件人'}</strong><time>{formatTime(active.receivedAt)}</time></div>
            <h3>{active.subject || '无主题'}</h3>
            {active.code ? <div className="code-proof"><span>验证码</span><strong>{active.code}</strong></div> : null}
            <pre>{active.bodyText || '邮件正文为空'}</pre>
            {active.actionUrl ? <div className="link-proof" aria-label="Claude 登录链接">
              <div className="link-proof-head">
                <span>Claude 登录链接</span>
                <div>
                  <a className="button button-primary" href={active.actionUrl} target="_blank" rel="noreferrer"><ExternalLink size={15} />link</a>
                  <button type="button" className="button button-secondary" onClick={() => void copyActionUrl(active.actionUrl || '')}><Clipboard size={15} />复制</button>
                </div>
              </div>
              <textarea readOnly value={active.actionUrl} rows={3} onFocus={(event) => event.currentTarget.select()} aria-label="完整链接" />
            </div> : null}
          </> : <div className="drawer-empty"><Inbox size={24} /><span>请选择一封邮件查看</span></div>}
        </div>
      </div>
    </aside>
  </div>;
}

